import React, { useState, useContext } from 'react'
import { Typography } from '@material-ui/core'
import DialogTitle from '@material-ui/core/DialogTitle'
import Dialog from '@material-ui/core/Dialog'
import Radio from '@material-ui/core/Radio'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import RadioGroup from '@material-ui/core/RadioGroup'
import CancelButton from 'app/components/buttons/CancelButton'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import { TextInput } from 'app/components/FormsyHelperComponent'
import { FormattedMessage, useIntl } from 'react-intl'
import * as R from 'ramda'
import effects from 'app/effects/effects'
import Formsy from 'formsy-react'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import { NotificationAction } from 'app/main/messages/notificationTimeline/notificationContext/NotificationActionProvider'

const CANCEL = 'CANCEL'
const DELETE = 'DELETE'

const effectByAction = R.cond([
  [
    R.propEq('action', CANCEL),
    ({ notificationId, cancellationDescription }) =>
      effects.apis.cancelEventNotification(
        notificationId,
        cancellationDescription,
      ),
  ],
  [
    R.propEq('action', DELETE),
    ({ notificationId }) => effects.apis.unsendNotification(notificationId),
  ],
])

function CancelEventNotification({ onClose, notificationId }) {
  const { withRefreshContent } = useContext(NotificationAction)
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [action, setAction] = useState(CANCEL)
  const [cancellationDescription, setCancellationDescription] = useState('')

  const handleFormChange = R.compose(
    setCancellationDescription,
    R.prop('cancellationDescription'),
  )

  const handleSubmit = () => {
    setIsSubmitting(true)
    withRefreshContent(
      { id: notificationId },
      effectByAction({ action, notificationId, cancellationDescription }),
    )
      .then(() => {
        dispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id:
                action === CANCEL
                  ? 'notificationTimeline.dialog.event.cancelDelete.cancel.success.alert'
                  : 'notificationTimeline.dialog.event.cancelDelete.delete.success.alert',
            }),
          }),
        )
        setIsSubmitting(false)
        onClose()
      })
      .catch((error) => {
        dispatch(MessageAlert.showNetworkError(formatMessage)(error))
        setIsSubmitting(false)
      })
  }

  return (
    <>
      <Dialog aria-labelledby="simple-dialog-title" open>
        <DialogTitle disableTypography>
          <Typography variant="h5" className="font-bold mt-12">
            <FormattedMessage id="notificationTimeline.dialog.event.cancelDelete.title" />
          </Typography>
        </DialogTitle>
        <List className="flex p-10 pt-0">
          <RadioGroup defaultValue={action}>
            <List>
              <ListItem className="flex flex-col items-start">
                <div>
                  <FormControlLabel
                    value={CANCEL}
                    onClick={() => setAction(CANCEL)}
                    control={<Radio color="primary" />}
                    label={
                      <Typography variant="h6">
                        <FormattedMessage id="notificationTimeline.dialog.event.button.cancel" />
                      </Typography>
                    }
                    labelPlacement="end"
                  />
                </div>
                <span className="ltr:ml-32 rtl:mr-32">
                  <FormattedMessage id="notificationTimeline.dialog.event.cancel.description" />
                </span>
              </ListItem>
              <ListItem className="flex flex-col items-start">
                <div>
                  <FormControlLabel
                    value={DELETE}
                    onClick={() => setAction(DELETE)}
                    control={<Radio color="primary" />}
                    label={
                      <Typography variant="h6">
                        <FormattedMessage id="notificationTimeline.dialog.event.button.delete" />
                      </Typography>
                    }
                    labelPlacement="end"
                  />
                </div>
                <span className="ltr:ml-32 rtl:mr-32">
                  <FormattedMessage id="notificationTimeline.dialog.event.delete.description" />
                </span>
              </ListItem>
            </List>
          </RadioGroup>
        </List>
        {action === CANCEL && (
          <div className="p-10">
            <Formsy onChange={handleFormChange}>
              <TextInput
                className="w-full"
                name="cancellationDescription"
                labelId="notificationTimeline.dialog.event.textfield.placeholder"
                placeholderId="notificationTimeline.dialog.event.textfield.placeholder"
                value={cancellationDescription}
                maxLength={100}
                variant="outlined"
                multiline
                autoFocus
                fullWidth
              />
            </Formsy>
          </div>
        )}
        <div className="flex justify-end p-10">
          <CancelButton
            className="ltr:mr-10 rtl:ml-10"
            onClick={onClose}
            disabled={isSubmitting}
          />
          <PrimaryButton
            labelId="action.confirm"
            isLoading={isSubmitting}
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
        </div>
      </Dialog>
    </>
  )
}

export default CancelEventNotification
